import { useMemo } from 'react';
import { TelemetrySparklineSvg } from '@/components/telemetry/TelemetrySparklineSvg';
import { useCanonicalTSB } from '@/hooks/useCanonicalTSB';

/**
 * Desktop-only "Live Vitals" sidebar panel for the Coach page.
 * Displays CTL/ATL/TSB summary with sparklines while chatting.
 *
 * TASK-021: Wired to canonical TSB source (useCanonicalTSB).
 * All values now read from GET /me/overview → DailyTrainingLoad table.
 */
export function LiveVitalsPanel() {
  const { data, isLoading } = useCanonicalTSB();

  const vitals = useMemo(() => {
    const normalizeSeries = (series: [string, number][]): number[] => {
      if (series.length === 0) return [0.5];
      const last10 = series.slice(-10);
      const values = last10.map(([, v]) => v);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      return values.map((v) => (v - min) / range);
    };

    const ctlTrend = data.ctlSeries.length >= 2
      ? (data.ctlSeries[data.ctlSeries.length - 1][1] >= data.ctlSeries[data.ctlSeries.length - 2][1] ? 'up' : 'down')
      : 'flat';
    const atlTrend = data.atlSeries.length >= 2
      ? (data.atlSeries[data.atlSeries.length - 1][1] >= data.atlSeries[data.atlSeries.length - 2][1] ? 'up' : 'down')
      : 'flat';
    const tsbTrend = data.tsbSeries.length >= 2
      ? (data.tsbSeries[data.tsbSeries.length - 1][1] >= data.tsbSeries[data.tsbSeries.length - 2][1] ? 'up' : 'down')
      : 'flat';

    return [
      {
        label: 'BASE',
        sublabel: 'Aerobic Base',
        value: data.ctl.toFixed(1),
        unit: '',
        trend: ctlTrend as 'up' | 'down' | 'flat',
        color: '#00FFC8',
        sparkline: normalizeSeries(data.ctlSeries),
      },
      {
        label: 'LOAD',
        sublabel: 'Recent Load',
        value: data.atl.toFixed(1),
        unit: '',
        trend: atlTrend as 'up' | 'down' | 'flat',
        color: 'hsl(35, 90%, 55%)',
        sparkline: normalizeSeries(data.atlSeries),
      },
      {
        label: 'FRESH',
        sublabel: 'Freshness Index',
        value: `${data.tsb > 0 ? '+' : ''}${data.tsb.toFixed(1)}`,
        unit: '',
        trend: tsbTrend as 'up' | 'down' | 'flat',
        color: 'hsl(200, 70%, 55%)',
        sparkline: normalizeSeries(data.tsbSeries),
      },
    ];
  }, [data]);

  return (
    <div className="w-64 shrink-0 border-l border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-4 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#00FFC8] shadow-[0_0_6px_rgba(0,255,200,0.6)] animate-pulse" />
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground/60">
          Live Vitals
        </span>
      </div>

      {/* Vital cards */}
      {isLoading ? (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3 animate-pulse">
              <div className="h-4 w-20 bg-white/[0.06] rounded mb-2" />
              <div className="h-6 w-12 bg-white/[0.06] rounded" />
            </div>
          ))}
        </div>
      ) : (
        vitals.map((v) => (
          <div
            key={v.label}
            className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground/50">
                  {v.sublabel}
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="font-mono text-lg font-medium" style={{ color: v.color }}>
                    {v.value}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground/40">
                    {v.label}
                  </span>
                </div>
              </div>
              <div
                className={`font-mono text-[9px] px-1.5 py-0.5 rounded ${
                  v.trend === 'up'
                    ? 'text-[#00FFC8] bg-[#00FFC8]/10'
                    : v.trend === 'down'
                      ? 'text-[hsl(35,90%,55%)] bg-[hsl(35,90%,55%)]/10'
                      : 'text-muted-foreground bg-muted/10'
                }`}
              >
                {v.trend === 'up' ? '▲' : v.trend === 'down' ? '▼' : '—'}
              </div>
            </div>
            <TelemetrySparklineSvg
              data={v.sparkline}
              width={200}
              height={28}
              stroke={v.color}
              className="w-full"
            />
          </div>
        ))
      )}

      {/* System status */}
      <div className="mt-auto pt-4 border-t border-white/[0.06]">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground/40">
            System Status
          </span>
          <span className="font-mono text-[9px] text-[#00FFC8]">NOMINAL</span>
        </div>
      </div>
    </div>
  );
}
